import React, { useState, useRef, useCallback, useEffect } from 'react';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { useToast } from '../ToastContext';
import { fmtTokens } from '../../utils/helpers';

// ─────────────────────────────────────────────────────────────────────────────
// WEB AUDIO — Mines sound effects
// ─────────────────────────────────────────────────────────────────────────────
class MinesAudio {
  constructor() {
    this.ctx = null;
    this.muted = false;
  }

  init() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (this.ctx.state === 'suspended') this.ctx.resume();
  }

  // Diamond reveal — ascending chime based on count
  tileReveal(count = 1) {
    if (this.muted || !this.ctx) return;
    const baseFreq = 500 + Math.min(count, 20) * 30;
    const now = this.ctx.currentTime;
    [baseFreq, baseFreq * 1.25].forEach((freq, i) => {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = 'triangle';
      o.frequency.value = freq;
      g.gain.setValueAtTime(0, now + i * 0.06);
      g.gain.linearRampToValueAtTime(0.08, now + i * 0.06 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.06 + 0.2);
      o.connect(g).connect(this.ctx.destination);
      o.start(now + i * 0.06);
      o.stop(now + i * 0.06 + 0.2);
    });
  }

  // Mine hit — low thud + noise burst
  mineHit() {
    if (this.muted || !this.ctx) return;
    const now = this.ctx.currentTime;
    // Low thud
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(250, now);
    o.frequency.exponentialRampToValueAtTime(40, now + 0.3);
    g.gain.setValueAtTime(0.25, now);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
    o.connect(g).connect(this.ctx.destination);
    o.start(now); o.stop(now + 0.3);
    // Noise burst
    const bufferSize = this.ctx.sampleRate * 0.25;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const filt = this.ctx.createBiquadFilter();
    filt.type = 'highpass'; filt.frequency.value = 800;
    const gn = this.ctx.createGain();
    gn.gain.setValueAtTime(0.15, now);
    gn.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
    src.connect(filt).connect(gn).connect(this.ctx.destination);
    src.start(now); src.stop(now + 0.25);
  }

  // Cashout win jingle
  cashout() {
    if (this.muted || !this.ctx) return;
    const notes = [523, 659, 784, 1047, 1319];
    const now = this.ctx.currentTime;
    notes.forEach((freq, i) => {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = 'triangle';
      o.frequency.value = freq;
      g.gain.setValueAtTime(0, now + i * 0.1);
      g.gain.linearRampToValueAtTime(0.1, now + i * 0.1 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.1 + 0.3);
      o.connect(g).connect(this.ctx.destination);
      o.start(now + i * 0.1); o.stop(now + i * 0.1 + 0.3);
    });
  }

  // UI click
  click() {
    if (this.muted || !this.ctx) return;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = 'square';
    o.frequency.value = 600;
    g.gain.setValueAtTime(0.04, this.ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.03);
    o.connect(g).connect(this.ctx.destination);
    o.start(); o.stop(this.ctx.currentTime + 0.03);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Multiplier calculation (client-side for display only, server is authoritative)
// ─────────────────────────────────────────────────────────────────────────────
function binomial(n, k) {
  if (k < 0 || k > n) return 0;
  if (k === 0 || k === n) return 1;
  if (k > n - k) k = n - k;
  let result = 1;
  for (let i = 0; i < k; i++) {
    result = result * (n - i) / (i + 1);
  }
  return result;
}

function calcMultiplier(mines, diamonds) {
  if (diamonds <= 0) return 1;
  const fair = binomial(25, diamonds) / binomial(25 - mines, diamonds);
  return parseFloat((0.99 * fair).toFixed(2));
}

// ─────────────────────────────────────────────────────────────────────────────
// ACCENT
// ─────────────────────────────────────────────────────────────────────────────
const ACCENT = '#00BFA5';
const ACCENT_DIM = 'rgba(0,191,165,0.15)';
const ACCENT_BORDER = 'rgba(0,191,165,0.5)';

const MINE_COUNT_PRESETS = [1, 3, 5, 10, 24];

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function MinesPage({ user, userData, onUserDataChange, onTokenAdjustmentChange, onBack, isMobile }) {
  const toast = useToast();
  const audioRef = useRef(new MinesAudio());

  // Cloud function refs
  const minesStartFn = useRef(httpsCallable(functions, 'minesStart')).current;
  const minesRevealFn = useRef(httpsCallable(functions, 'minesReveal')).current;
  const minesCashoutFn = useRef(httpsCallable(functions, 'minesCashout')).current;
  const minesGetActiveFn = useRef(httpsCallable(functions, 'minesGetActive')).current;

  // Game state
  const [gameActive, setGameActive] = useState(false);
  const [mineCount, setMineCount] = useState(3);
  const [revealedTiles, setRevealedTiles] = useState(new Map()); // index -> 'diamond'
  const [minePositions, setMinePositions] = useState(null); // shown only when game ends
  const [currentMultiplier, setCurrentMultiplier] = useState(1);
  const [diamondsRevealed, setDiamondsRevealed] = useState(0);

  // Bet state
  const [betAmount, setBetAmount] = useState('1');
  const [tokenType, setTokenType] = useState('crewmate');

  // UI state
  const [loading, setLoading] = useState(false);
  const [cashingOut, setCashingOut] = useState(false);
  const [muted, setMuted] = useState(false);
  const [gameOverResult, setGameOverResult] = useState(null); // 'mine' | 'cashout' | null
  const [hitTileIndex, setHitTileIndex] = useState(null);
  const [lastPayout, setLastPayout] = useState(null);
  const [recovering, setRecovering] = useState(false);
  const [showPaytable, setShowPaytable] = useState(false);

  // Animation tracking
  const [animatingTile, setAnimatingTile] = useState(null);
  const [pendingTiles, setPendingTiles] = useState(new Set()); // tile indices awaiting server response
  const inflightTiles = useRef(new Set()); // tiles with in-flight server requests

  // Mute toggle
  useEffect(() => { audioRef.current.muted = muted; }, [muted]);

  // Recover active game on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await minesGetActiveFn();
        if (cancelled) return;
        const data = res.data;
        if (data.active) {
          setGameActive(true);
          setMineCount(data.mineCount);
          setBetAmount(String(data.betAmount));
          setTokenType(data.tokenType);
          setCurrentMultiplier(data.currentMultiplier);
          setDiamondsRevealed(data.revealed.length);
          const map = new Map();
          data.revealed.forEach(idx => map.set(idx, 'diamond'));
          setRevealedTiles(map);
        }
      } catch (err) {
        // No active game or error — ignore
      } finally {
        if (!cancelled) setRecovering(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const balance = tokenType === 'crewmate'
    ? (userData?.crewmateTokens ?? 0)
    : (userData?.imposterTokens ?? 0);

  const handleStartGame = useCallback(async () => {
    const bet = parseFloat(betAmount);
    if (!Number.isFinite(bet) || bet < 0.1) { toast.error('Minimum bet is 0.1 tokens.'); return; }
    if (bet > 10000) { toast.error('Maximum bet is 10,000 tokens.'); return; }
    if (bet > balance) { toast.error('Insufficient balance.'); return; }

    audioRef.current.init();
    audioRef.current.click();
    setLoading(true);

    try {
      await minesStartFn({ betAmount: bet, tokenType, mineCount });
      setGameActive(true);
      setRevealedTiles(new Map());
      setMinePositions(null);
      setCurrentMultiplier(1);
      setDiamondsRevealed(0);
      setGameOverResult(null);
      setHitTileIndex(null);
      setLastPayout(null);
      // Optimistic balance update — Nav subtracts tokenAdjustment from userData,
      // so pass positive bet to show deducted balance. Freeze to block auto-refresh
      // (server has already deducted, so a refresh would double-deduct the display).
      if (onTokenAdjustmentChange) {
        onTokenAdjustmentChange({
          crewmate: tokenType === 'crewmate' ? bet : 0,
          imposter: tokenType === 'imposter' ? bet : 0,
          frozen: true,
        });
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to start game.');
    } finally {
      setLoading(false);
    }
  }, [betAmount, tokenType, mineCount, balance, minesStartFn, toast, onTokenAdjustmentChange]);

  const handleRevealTile = useCallback(async (index) => {
    if (!gameActive || cashingOut || revealedTiles.has(index) || gameOverResult) return;
    if (inflightTiles.current.has(index)) return; // already in-flight

    audioRef.current.init();
    inflightTiles.current.add(index);

    // --- Show pending state (pulsating tile) while waiting for server ---
    setPendingTiles(prev => new Set(prev).add(index));

    try {
      const res = await minesRevealFn({ tileIndex: index });
      const data = res.data;

      setPendingTiles(prev => { const next = new Set(prev); next.delete(index); return next; });

      // Use server-authoritative multiplier
      setCurrentMultiplier(data.currentMultiplier);
      setDiamondsRevealed(data.diamondsRevealed);

      if (data.result === 'diamond') {
        audioRef.current.tileReveal(data.diamondsRevealed);
        setRevealedTiles(prev => new Map(prev).set(index, 'diamond'));
        setAnimatingTile(index);
        setTimeout(() => setAnimatingTile(null), 300);

        if (data.autoCashout) {
          setMinePositions(data.minePositions);
          setLastPayout(data.payout);
          setGameOverResult('cashout');
          setGameActive(false);
          audioRef.current.cashout();
          toast.success(`Auto-cashout! Won ${fmtTokens(data.payout)} tokens at ${data.currentMultiplier}x`);
          if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: 0, imposter: 0, frozen: false });
          if (onUserDataChange) onUserDataChange();
        }
      } else if (data.result === 'mine') {
        audioRef.current.mineHit();
        setHitTileIndex(index);
        setMinePositions(data.minePositions);
        setGameOverResult('mine');
        setGameActive(false);
        if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: 0, imposter: 0, frozen: false });
        if (onUserDataChange) onUserDataChange();
      }
    } catch (err) {
      setPendingTiles(prev => { const next = new Set(prev); next.delete(index); return next; });
      toast.error(err?.message || 'Failed to reveal tile.');
    } finally {
      inflightTiles.current.delete(index);
    }
  }, [gameActive, cashingOut, revealedTiles, gameOverResult, diamondsRevealed, mineCount, minesRevealFn, toast, onUserDataChange, onTokenAdjustmentChange]);

  const handleCashout = useCallback(async () => {
    if (!gameActive || cashingOut || diamondsRevealed === 0) return;

    audioRef.current.init();
    setCashingOut(true);

    try {
      const res = await minesCashoutFn();
      const data = res.data;
      audioRef.current.cashout();
      setMinePositions(data.minePositions);
      setLastPayout(data.payout);
      setGameOverResult('cashout');
      setGameActive(false);
      toast.success(`Cashed out ${fmtTokens(data.payout)} tokens at ${data.multiplier}x!`);
      if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: 0, imposter: 0, frozen: false });
      if (onUserDataChange) onUserDataChange();
    } catch (err) {
      toast.error(err?.message || 'Failed to cash out.');
    } finally {
      setCashingOut(false);
    }
  }, [gameActive, cashingOut, diamondsRevealed, minesCashoutFn, toast, onUserDataChange, onTokenAdjustmentChange]);

  const handleNewGame = useCallback(() => {
    setGameActive(false);
    setRevealedTiles(new Map());
    setMinePositions(null);
    setCurrentMultiplier(1);
    setDiamondsRevealed(0);
    setGameOverResult(null);
    setHitTileIndex(null);
    setLastPayout(null);
  }, []);

  // ─── Tile rendering helper ──────────────────────────────────────────────────
  const renderTile = (index) => {
    const isDiamond = revealedTiles.has(index);
    const isHitMine = hitTileIndex === index;
    const isRevealedMine = !isDiamond && !isHitMine && minePositions && minePositions.includes(index) && gameOverResult;
    const isAnimating = animatingTile === index;
    const isPending = pendingTiles.has(index);
    const canClick = gameActive && !cashingOut && !isDiamond && !gameOverResult && !isPending;

    let bg = 'linear-gradient(145deg, #1a1a2e 0%, #16162a 100%)';
    let border = '2px solid rgba(255,255,255,0.06)';
    let content = '';
    let shadow = 'inset 0 1px 0 rgba(255,255,255,0.03)';
    let animStyle = {};

    if (isPending) {
      bg = `linear-gradient(145deg, rgba(0,191,165,0.12) 0%, rgba(0,120,100,0.08) 100%)`;
      border = `2px solid ${ACCENT_BORDER}`;
      shadow = `0 0 10px rgba(0,191,165,0.15)`;
      animStyle = { animation: 'minesTilePending 0.6s ease-in-out infinite' };
    } else if (isDiamond) {
      bg = `linear-gradient(145deg, rgba(0,191,165,0.25) 0%, rgba(0,120,100,0.2) 100%)`;
      border = `2px solid ${ACCENT_BORDER}`;
      content = '💎';
      shadow = `0 0 12px rgba(0,191,165,0.3), inset 0 1px 0 rgba(255,255,255,0.05)`;
      if (isAnimating) animStyle = { animation: 'minesDiamondReveal 0.3s ease-out' };
    } else if (isHitMine) {
      bg = 'linear-gradient(145deg, rgba(232,56,74,0.4) 0%, rgba(180,30,50,0.3) 100%)';
      border = '2px solid rgba(232,56,74,0.7)';
      content = '💣';
      shadow = '0 0 20px rgba(232,56,74,0.4)';
      animStyle = { animation: 'minesMineExplode 0.4s ease-out' };
    } else if (isRevealedMine) {
      bg = 'linear-gradient(145deg, rgba(232,56,74,0.15) 0%, rgba(180,30,50,0.1) 100%)';
      border = '2px solid rgba(232,56,74,0.3)';
      content = '💣';
      shadow = 'none';
      const mineIdx = minePositions.indexOf(index);
      animStyle = { animation: `minesFadeIn 0.3s ease-out ${mineIdx * 0.08}s both` };
    }

    return (
      <div
        key={index}
        onClick={() => canClick && handleRevealTile(index)}
        style={{
          width: '100%',
          aspectRatio: '1',
          borderRadius: '10px',
          background: bg,
          border,
          boxShadow: shadow,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: isMobile ? '22px' : '28px',
          cursor: canClick ? 'pointer' : 'default',
          transition: canClick ? 'transform 0.1s, box-shadow 0.1s, background 0.15s' : 'none',
          userSelect: 'none',
          position: 'relative',
          ...animStyle,
        }}
        onMouseEnter={e => {
          if (!canClick) return;
          e.currentTarget.style.transform = 'scale(1.06)';
          e.currentTarget.style.boxShadow = `0 0 16px rgba(0,191,165,0.2), inset 0 1px 0 rgba(255,255,255,0.06)`;
          e.currentTarget.style.background = 'linear-gradient(145deg, #222244 0%, #1a1a36 100%)';
        }}
        onMouseLeave={e => {
          // Always reset — prevents stuck hover when canClick changes mid-hover
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = shadow;
          e.currentTarget.style.background = bg;
        }}
      >
        {content}
      </div>
    );
  };

  // ─── Next multiplier for preview ────────────────────────────────────────────
  const nextMultiplier = gameActive && !gameOverResult
    ? calcMultiplier(mineCount, diamondsRevealed + 1)
    : null;
  const potentialPayout = gameActive && diamondsRevealed > 0
    ? parseFloat((parseFloat(betAmount) * currentMultiplier).toFixed(2))
    : null;

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{
      position: isMobile ? 'relative' : 'fixed',
      top: isMobile ? 0 : 0,
      left: isMobile ? undefined : 'var(--sidebar-w)',
      right: isMobile ? undefined : 0,
      bottom: isMobile ? undefined : 0,
      display: 'flex', flexDirection: 'column',
      background: 'var(--bg-primary)',
      overflow: isMobile ? 'visible' : 'auto',
      minHeight: isMobile ? 'calc(100vh - 48px - 60px)' : undefined,
      paddingBottom: isMobile ? '20px' : 0,
    }}>
      {/* Keyframe animations */}
      <style>{`
        @keyframes minesDiamondReveal {
          0% { transform: scale(0.5); opacity: 0; }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes minesMineExplode {
          0% { transform: scale(0.3) rotate(-10deg); opacity: 0; }
          40% { transform: scale(1.3) rotate(5deg); opacity: 1; }
          100% { transform: scale(1) rotate(0deg); }
        }
        @keyframes minesFadeIn {
          0% { opacity: 0; transform: scale(0.8); }
          100% { opacity: 0.7; transform: scale(1); }
        }
        @keyframes minesCashoutPulse {
          0%, 100% { box-shadow: 0 0 10px rgba(0,230,118,0.3); }
          50% { box-shadow: 0 0 25px rgba(0,230,118,0.5), 0 0 50px rgba(0,230,118,0.15); }
        }
        @keyframes minesResultPop {
          0% { transform: scale(0.8); opacity: 0; }
          60% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes minesTilePending {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(0.95); opacity: 1; }
        }
      `}</style>

      {/* Paytable Modal */}
      {showPaytable && (() => {
        const mineOptions = [1, 2, 3, 4, 5, 7, 10, 15, 20, 24];
        const maxDiamonds = (m) => 25 - m;
        return (
          <div
            onClick={() => setShowPaytable(false)}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.8)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', zIndex: 9999,
              padding: '20px',
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: '#111118', borderRadius: '16px',
                border: `1px solid ${ACCENT_BORDER}`,
                boxShadow: `0 8px 40px rgba(0,0,0,0.5), 0 0 20px rgba(0,191,165,0.1)`,
                maxWidth: '680px', width: '100%',
                maxHeight: isMobile ? '80vh' : '85vh',
                overflow: 'auto', padding: isMobile ? '16px' : '24px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: ACCENT }}>
                  💎 Mines Paytable
                </h3>
                <button onClick={() => setShowPaytable(false)} style={{
                  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px', color: '#aaa', cursor: 'pointer',
                  padding: '4px 10px', fontSize: '16px',
                }}>✕</button>
              </div>
              <p style={{ fontSize: '12px', color: '#888', marginBottom: '16px', lineHeight: 1.5 }}>
                Multipliers based on mine count and diamonds revealed. House edge: 1%.
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? '11px' : '12px',
                }}>
                  <thead>
                    <tr>
                      <th style={{
                        padding: '8px 6px', textAlign: 'center', color: ACCENT,
                        borderBottom: `2px solid ${ACCENT_BORDER}`, fontWeight: '700',
                        position: 'sticky', left: 0, background: '#111118', zIndex: 1,
                        fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px',
                      }}>Mines →<br/>Gems ↓</th>
                      {mineOptions.map(m => (
                        <th key={m} style={{
                          padding: '8px 6px', textAlign: 'center',
                          borderBottom: `2px solid ${ACCENT_BORDER}`,
                          color: mineCount === m ? ACCENT : '#aaa', fontWeight: '700',
                          background: mineCount === m ? 'rgba(0,191,165,0.08)' : 'transparent',
                        }}>{m}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 24 }, (_, d) => d + 1).map(d => {
                      const hasAnyValue = mineOptions.some(m => d <= maxDiamonds(m));
                      if (!hasAnyValue) return null;
                      return (
                        <tr key={d}>
                          <td style={{
                            padding: '6px', textAlign: 'center', fontWeight: '700',
                            color: '#aaa', borderBottom: '1px solid rgba(255,255,255,0.04)',
                            position: 'sticky', left: 0, background: '#111118', zIndex: 1,
                          }}>{d}</td>
                          {mineOptions.map(m => {
                            if (d > maxDiamonds(m)) {
                              return <td key={m} style={{
                                padding: '6px', textAlign: 'center',
                                borderBottom: '1px solid rgba(255,255,255,0.04)',
                                color: '#333',
                              }}>—</td>;
                            }
                            const mult = calcMultiplier(m, d);
                            const isHigh = mult >= 100;
                            return (
                              <td key={m} style={{
                                padding: '6px', textAlign: 'center',
                                borderBottom: '1px solid rgba(255,255,255,0.04)',
                                color: isHigh ? '#FFD700' : (mult >= 10 ? ACCENT : '#ccc'),
                                fontWeight: isHigh ? '800' : (mult >= 10 ? '700' : '500'),
                                background: mineCount === m ? 'rgba(0,191,165,0.04)' : 'transparent',
                                fontSize: mult >= 1000 ? (isMobile ? '9px' : '10px') : undefined,
                              }}>{mult >= 1000 ? mult.toLocaleString() + 'x' : mult + 'x'}</td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: isMobile ? '12px 12px 8px' : '16px 24px 12px',
        flexShrink: 0,
      }}>
        <button onClick={onBack} style={{
          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '8px', color: '#aaa', cursor: 'pointer',
          padding: '6px 12px', fontSize: '13px',
        }}>← Back</button>
        <h2 style={{
          flex: 1, margin: 0, fontSize: isMobile ? '18px' : '22px',
          fontWeight: '800', letterSpacing: '2px', textTransform: 'uppercase',
          color: ACCENT,
        }}>💎 Mines</h2>
        <div style={{
          color: '#ccc', fontSize: '12px', marginRight: '8px',
        }}>
          {tokenType === 'crewmate' ? '🔵' : '🔴'} {fmtTokens(balance)}
        </div>
        <button onClick={() => setShowPaytable(true)} style={{
          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '8px', color: '#aaa', cursor: 'pointer',
          padding: '6px 10px', fontSize: '14px', fontWeight: '700',
        }} title="Paytable">ℹ</button>
        <button onClick={() => setMuted(m => !m)} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: '18px', padding: '4px',
        }}>
          {muted ? '🔇' : '🔊'}
        </button>
      </div>

      {/* Main area: grid + controls */}
      <div style={{
        flex: 1, display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '16px' : '24px',
        padding: isMobile ? '8px 12px' : '8px 24px 24px',
        alignItems: isMobile ? 'center' : 'flex-start',
        justifyContent: 'center',
      }}>
        {/* 5×5 Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: isMobile ? '6px' : '8px',
          width: isMobile ? 'calc(100vw - 40px)' : '400px',
          maxWidth: '400px',
          flexShrink: 0,
        }}>
          {Array.from({ length: 25 }, (_, i) => renderTile(i))}
        </div>

        {/* Controls panel */}
        <div style={{
          width: isMobile ? '100%' : '280px',
          maxWidth: isMobile ? '400px' : '280px',
          display: 'flex', flexDirection: 'column', gap: '14px',
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '16px',
          padding: isMobile ? '16px' : '20px',
        }}>
          {/* Mine count selector */}
          <div>
            <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', fontWeight: '600' }}>
              Mines
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {MINE_COUNT_PRESETS.map(n => (
                <button
                  key={n}
                  onClick={() => { if (!gameActive) { setMineCount(n); audioRef.current.init(); audioRef.current.click(); } }}
                  disabled={gameActive}
                  style={{
                    padding: '6px 12px', borderRadius: '8px', cursor: gameActive ? 'not-allowed' : 'pointer',
                    fontSize: '13px', fontWeight: '700',
                    background: mineCount === n ? ACCENT_DIM : 'rgba(255,255,255,0.04)',
                    border: `1.5px solid ${mineCount === n ? ACCENT_BORDER : 'rgba(255,255,255,0.08)'}`,
                    color: mineCount === n ? ACCENT : '#aaa',
                    opacity: gameActive ? 0.5 : 1,
                    transition: 'all 0.15s',
                  }}
                >
                  {n}
                </button>
              ))}
              {/* Custom mine count input */}
              <input
                type="number"
                min={1}
                max={24}
                value={mineCount}
                disabled={gameActive}
                onChange={e => {
                  const v = parseInt(e.target.value, 10);
                  if (v >= 1 && v <= 24) setMineCount(v);
                }}
                style={{
                  width: '52px', padding: '6px 8px', borderRadius: '8px',
                  background: 'rgba(255,255,255,0.04)', border: '1.5px solid rgba(255,255,255,0.1)',
                  color: '#ddd', fontSize: '13px', fontWeight: '600', textAlign: 'center',
                  outline: 'none', opacity: gameActive ? 0.5 : 1,
                }}
              />
            </div>
          </div>

          {/* Bet amount */}
          <div>
            <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', fontWeight: '600' }}>
              Bet Amount
            </div>
            <input
              type="number"
              min={0.1}
              step={0.1}
              value={betAmount}
              disabled={gameActive}
              onChange={e => setBetAmount(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px',
                background: 'rgba(255,255,255,0.04)', border: '1.5px solid rgba(255,255,255,0.1)',
                color: '#ddd', fontSize: '16px', fontWeight: '600', outline: 'none',
                boxSizing: 'border-box', opacity: gameActive ? 0.5 : 1,
              }}
            />
            <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
              {[
                { label: '½', fn: () => setBetAmount(v => String(Math.max(0.1, parseFloat(v) / 2 || 0.1))) },
                { label: '2x', fn: () => setBetAmount(v => String(Math.min(10000, (parseFloat(v) || 1) * 2))) },
                { label: 'Max', fn: () => setBetAmount(String(Math.min(10000, balance))) },
              ].map(b => (
                <button
                  key={b.label}
                  onClick={() => { if (!gameActive) b.fn(); }}
                  disabled={gameActive}
                  style={{
                    padding: '5px 10px', borderRadius: '6px', cursor: gameActive ? 'not-allowed' : 'pointer',
                    fontSize: '11px', fontWeight: '700',
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                    color: '#aaa', opacity: gameActive ? 0.5 : 1,
                  }}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>

          {/* Token type toggle */}
          <div>
            <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', fontWeight: '600' }}>
              Token
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {['crewmate', 'imposter'].map(t => (
                <button
                  key={t}
                  onClick={() => { if (!gameActive) setTokenType(t); }}
                  disabled={gameActive}
                  style={{
                    flex: 1, padding: '8px', borderRadius: '8px',
                    cursor: gameActive ? 'not-allowed' : 'pointer',
                    fontSize: '12px', fontWeight: '700',
                    background: tokenType === t ? (t === 'crewmate' ? 'rgba(58,143,212,0.15)' : 'rgba(232,56,74,0.15)') : 'rgba(255,255,255,0.04)',
                    border: `1.5px solid ${tokenType === t ? (t === 'crewmate' ? 'rgba(58,143,212,0.5)' : 'rgba(232,56,74,0.5)') : 'rgba(255,255,255,0.08)'}`,
                    color: tokenType === t ? (t === 'crewmate' ? '#3A8FD4' : '#E8384A') : '#777',
                    opacity: gameActive ? 0.5 : 1,
                  }}
                >
                  {t === 'crewmate' ? '🔵 Crewmate' : '🔴 Imposter'}
                </button>
              ))}
            </div>
          </div>

          {/* Multiplier display (during game) */}
          {gameActive && !gameOverResult && (
            <div style={{
              textAlign: 'center', padding: '12px',
              background: 'rgba(0,0,0,0.2)', borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.05)',
            }}>
              {diamondsRevealed > 0 ? (
                <>
                  <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                    Current Multiplier
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: '800', color: ACCENT }}>
                    {currentMultiplier}x
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                    Payout: {tokenType === 'crewmate' ? '🔵' : '🔴'} {fmtTokens(potentialPayout)}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                    Next Tile
                  </div>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: '#aaa' }}>
                    {nextMultiplier}x
                  </div>
                </>
              )}
              {diamondsRevealed > 0 && nextMultiplier && diamondsRevealed < 25 - mineCount && (
                <div style={{ fontSize: '11px', color: '#666', marginTop: '6px' }}>
                  Next: {nextMultiplier}x
                </div>
              )}
            </div>
          )}

          {/* Game over result */}
          {gameOverResult && (
            <div style={{
              textAlign: 'center', padding: '16px',
              background: gameOverResult === 'mine' ? 'rgba(232,56,74,0.1)' : 'rgba(0,230,118,0.1)',
              borderRadius: '10px',
              border: `1px solid ${gameOverResult === 'mine' ? 'rgba(232,56,74,0.3)' : 'rgba(0,230,118,0.3)'}`,
              animation: 'minesResultPop 0.3s ease-out',
            }}>
              {gameOverResult === 'mine' ? (
                <>
                  <div style={{ fontSize: '24px', marginBottom: '4px' }}>💥</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#E8384A' }}>Mine Hit!</div>
                  <div style={{ fontSize: '13px', color: '#aaa', marginTop: '4px' }}>
                    Lost {tokenType === 'crewmate' ? '🔵' : '🔴'} {fmtTokens(parseFloat(betAmount))}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '24px', marginBottom: '4px' }}>🎉</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#00E676' }}>Cashed Out!</div>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: ACCENT, marginTop: '4px' }}>
                    {tokenType === 'crewmate' ? '🔵' : '🔴'} {fmtTokens(lastPayout)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                    at {currentMultiplier}x
                  </div>
                </>
              )}
            </div>
          )}

          {/* Action button */}
          {!gameActive && !gameOverResult && (
            <button
              onClick={handleStartGame}
              disabled={loading}
              style={{
                width: '100%', padding: '14px', borderRadius: '10px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px',
                background: loading ? '#333' : `linear-gradient(135deg, ${ACCENT}, #009688)`,
                border: 'none', color: '#fff',
                boxShadow: loading ? 'none' : `0 4px 20px rgba(0,191,165,0.3)`,
                opacity: loading ? 0.6 : 1,
                transition: 'all 0.2s',
              }}
            >
              {loading ? 'Starting...' : 'Bet & Start'}
            </button>
          )}

          {gameActive && !gameOverResult && (
            <button
              onClick={handleCashout}
              disabled={diamondsRevealed === 0 || cashingOut || loading}
              style={{
                width: '100%', padding: '14px', borderRadius: '10px',
                cursor: (diamondsRevealed === 0 || cashingOut || loading) ? 'not-allowed' : 'pointer',
                fontSize: '14px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px',
                background: diamondsRevealed > 0 ? 'linear-gradient(135deg, #00E676, #00C853)' : '#333',
                border: 'none', color: '#fff',
                boxShadow: diamondsRevealed > 0 ? '0 4px 20px rgba(0,230,118,0.3)' : 'none',
                opacity: (diamondsRevealed === 0 || cashingOut || loading) ? 0.5 : 1,
                animation: diamondsRevealed > 0 && !cashingOut && !loading ? 'minesCashoutPulse 2s ease-in-out infinite' : 'none',
                transition: 'all 0.2s',
              }}
            >
              {cashingOut ? 'Cashing out...' : diamondsRevealed > 0 ? `Cashout ${fmtTokens(potentialPayout)}` : 'Reveal a tile first'}
            </button>
          )}

          {gameOverResult && (
            <button
              onClick={handleNewGame}
              style={{
                width: '100%', padding: '14px', borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '14px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px',
                background: `linear-gradient(135deg, ${ACCENT}40, ${ACCENT}15)`,
                border: `1.5px solid ${ACCENT_BORDER}`,
                color: ACCENT,
                transition: 'all 0.2s',
              }}
            >
              New Game
            </button>
          )}

          {/* Game info */}
          {gameActive && !gameOverResult && (
            <div style={{ textAlign: 'center', fontSize: '11px', color: '#555' }}>
              {diamondsRevealed} / {25 - mineCount} diamonds revealed · {mineCount} mines
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MinesPage;
